import { computed, inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DocumentRecord } from "@models/annual-statements/document-record";
import { Company } from "@models/companies/company";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { Relationship } from "@models/relationships/relationship";
import { SecurityRegistryRecord } from "@models/shares/security-registry-record";
import { Trust } from "@models/trusts/trust";
import { AppointmentType, TrustChangeRelationship } from "@models/trusts/trust-change-relationship";
import { TrustType } from "@models/trusts/trusts-enums";
import { IDetailTab } from "@models/ui-models/detail-tab";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { patchState, signalState } from "@ngrx/signals";
import {
  IShareholdersPatchModel,
  IShareholdersState,
  IShareholdersViewModel
} from "@pages/companies/models/company-shareholders-state.interface";
import { TrustProfileTabs } from "@pages/trusts/components/trust-profile-page/trust-profile-page.component";
import {
  FormEditTrustDetailsComponent
} from "@pages/trusts/components/trusts-page/components/form-edit-trust-details/form-edit-trust-details.component";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import {
  SecurityholderViewModel
} from "@shared/components/entity-profiles/entity-securityholders-view/securityholder-view-model";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { EntityIdentityApiService } from "@shared/services/entity-identity-api.service";
import { NotesApiService } from "@shared/services/notes-api.service";
import { SecurityApiService } from "@shared/services/security-api.service";
import { TrustsApiService } from "@shared/services/trusts-api.service";
import { doNothing } from "@shared/utils/doNothing";
import { checkIsDocumentRecordInSigning } from "@shared/utils/is-document-in-signing";
import { ToastrService } from "ngx-toastr";
import { combineLatest, map, Observable, of } from "rxjs";
import { BaseStepperAppliableComponent } from "../../../forms/asic-forms/stepper-form";
import {
  FormChangeOfAppointorComponent,
  FormChangeOfTrusteeComponent,
  FormTrustDistributionComponent
} from "../../../forms/asic-forms/trusts";
import {
  TrustDistribution
} from "../../../forms/asic-forms/trusts/form-distribution-resolution-or-minutes/form-distribution-resolution-or-minutes.model";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

export type TrustProfileLoadingKeys = 'isTrustLoading'
  | 'isPendingDocumentsLoading'
  | 'isShareholdersLoading'
  | 'isIsXpmConnectedLoading';

export interface ITrustProfileState {
  loaders: Record<TrustProfileLoadingKeys, boolean>;

  isPendingChangesVisible: boolean;
  isPastRelationshipsVisible: boolean;

  trustId: string,
  trustWithPendingChanges: Trust;
  lodgedTrust: Trust;

  unitholdersBeneficiaries: IShareholdersState;

  pendingDocumentRecords: DocumentRecord[];

  isXpmConnected: boolean;
  notesTotal: number;
}

const trustProfileInitialState: ITrustProfileState = {
  loaders: {
    isTrustLoading: false,
    isPendingDocumentsLoading: false,
    isShareholdersLoading: false,
    isIsXpmConnectedLoading: false,
  },

  isPendingChangesVisible: true,
  isPastRelationshipsVisible: false,

  trustId: '',
  trustWithPendingChanges: new Trust(),
  lodgedTrust: new Trust(),

  unitholdersBeneficiaries: {
    isLoaded: false,
    isPastShareholdersVisible: false,
    shareholdersWithPendingChanges: [],
    lodgedShareholders: [],
    shareholderDetails: null,
  },

  pendingDocumentRecords: [],

  isXpmConnected: false,
  notesTotal: 0
};

@Injectable()
export class TrustProfileStateService {
  private trustsApiService = inject(TrustsApiService);
  private documentsApiService = inject(DocumentsApiService);
  private entityIdentityApiService = inject(EntityIdentityApiService);
  private securityApiService = inject(SecurityApiService);
  private notesApiService = inject(NotesApiService);

  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  readonly trustsWebformCloseConfirmationMessage = 'Are you sure you want to close the form? The data will not be saved.';

  state = signalState<ITrustProfileState>(trustProfileInitialState);

  // =====  Selectors  =====

  modalOpened = this.modalFormsService.modalOpened;
  trustAlerts = computed(() => this.state.trustWithPendingChanges().alerts);
  tabs = computed(() => {
    const trust = this.state.trustWithPendingChanges();
    let tabs: IDetailTab<TrustProfileTabs>[] = [
      { title: 'Details' },
      { title: 'Trustee' },
      { title: 'Beneficiaries' },
      { title: 'Relationships' },
      { title: 'Documents' },
    ];

    if (trust.trustType == TrustType.Unit) {
      tabs = tabs.map(tab => {
        if (tab.title === 'Beneficiaries') {
          return { title: 'UnitHolders' };
        }
        return tab;
      });
    }

    return tabs;
  });

  trustViewModel = computed<Trust>(() => this.state.isPendingChangesVisible() ? this.state.trustWithPendingChanges() : this.state.lodgedTrust());

  trustRelationships = computed<Relationship[]>(() => {
    // all trust relationships except for Trustee and Beneficiary
    const trust = this.trustViewModel();
    const trustRelationships: Relationship[] = [];
    const isPastRelationshipsVisible = this.state.isPastRelationshipsVisible();

    if (trust.appointors) trustRelationships.push(...trust.appointors);
    if (trust.settlor) trustRelationships.push(trust.settlor);
    if (trust.unitHolders.length) trustRelationships.push(...trust.unitHolders);

    if (isPastRelationshipsVisible) {
      return trustRelationships;
    }

    return trustRelationships.filter((relationship) => !relationship.end);
  });

  unitholdersBeneficiaryViewModel = computed<IShareholdersViewModel>(() => {
    const isPendingChangesVisible = this.state.isPendingChangesVisible();
    const isPastShareholdersVisible = this.state.unitholdersBeneficiaries.isPastShareholdersVisible();
    const shareholderDetails = this.state.unitholdersBeneficiaries.shareholderDetails();
    const shareholdersWithPendingChanges = this.state.unitholdersBeneficiaries.shareholdersWithPendingChanges();
    const lodgedShareholders = this.state.unitholdersBeneficiaries.lodgedShareholders();

    const viewModel = shareholdersWithPendingChanges.map((record) => {
      const model = new SecurityholderViewModel({ record });
      const relatedPreviousRecord = lodgedShareholders.find((previousRecord) => {
        return previousRecord.holders.every((previousHolder) => previousHolder.relationshipId == (record.holders.find((holder) => holder.relationshipId == previousHolder.relationshipId)?.relationshipId));
      });

      if (relatedPreviousRecord) {
        model.fillPreviousRecord(relatedPreviousRecord);
      }

      return model;
    });

    const currentShareholders = viewModel.filter((model) => !model.isPastShareholder());
    const pastShareholders = viewModel.filter((model) => model.isPastShareholder());

    const isShowPastShareholdersToggleVisible = pastShareholders.length > 0;

    const shareholders: (string | SecurityholderViewModel)[] = [
      'Current Shareholders',
      ...currentShareholders,
    ];

    if (isPastShareholdersVisible) {
      shareholders.push(
        'Past Shareholders',
        ...pastShareholders
      );
    }

    return {
      isPendingChangesVisible,
      isPastShareholdersVisible,
      isShowPastShareholdersToggleVisible,
      shareholderDetails,
      shareholders,
    };
  });

  // =====  State Mutations  =====

  setIsLoading(key: TrustProfileLoadingKeys, isLoading = true): void {
    patchState(this.state, (state) => ({ loaders: { ...state.loaders, [key]: isLoading } }));
  }

  setIsPendingChangesVisible(isPendingChangesVisible = true): void {
    patchState(this.state, () => ({ isPendingChangesVisible }));
  }

  setIsPastRelationshipsVisible(isPastRelationshipsVisible = true): void {
    patchState(this.state, () => ({ isPastRelationshipsVisible }));
  }

  setNotesTotal(notesTotal: number): void {
    patchState(this.state, () => ({ notesTotal }));
  }

  patchShareholdersState(shareholdersPatchModel: IShareholdersPatchModel): void {
    patchState(this.state, (state) => ({ unitholdersBeneficiaries: { ...state.unitholdersBeneficiaries, ...shareholdersPatchModel } }));
  }

  // =====  Life Cycle  =====

  init(trustId: string): void {
    patchState(this.state, () => ({ trustId }));

    this.loadTrust();
    this.loadDocuments();
    this.loadIsXpmConnected();
    this.loadNotesTotal();
  }

  onSwitchOnTab(tabName: TrustProfileTabs): void {
    switch (tabName) {
      case 'Unitholders' :
      case 'UnitHolders' :
      case 'Beneficiaries' : {
        if (this.state.unitholdersBeneficiaries.isLoaded()) return;
        this.loadUnitholdersBeneficiary();
        break;
      }
    }
  }

  // =====  Data Loading  =====

  loadTrust(): void {
    this.setIsLoading('isTrustLoading');

    const loadTrustWithPendingChangesRequest = this.getTrust();
    const loadLodgedTrustRequest = this.getTrust(false);

    combineLatest(loadTrustWithPendingChangesRequest, loadLodgedTrustRequest)
      .subscribe({
        next: ([trustWithPendingChanges, lodgedTrust]) => {
          patchState(this.state, () => ({ trustWithPendingChanges, lodgedTrust }));
          this.setIsLoading('isTrustLoading', false);
        },
        error: (error) => {
          console.warn('[loadTrust] error: ', error);
          this.toastr.error('Failed to load trust profile', 'Error');
          void this.router.navigate(['/trusts']);
        }
      });
  }

  loadUnitholdersBeneficiary(): void {
    this.setIsLoading('isShareholdersLoading');

    const loadUnitholdersBeneficiaryWithPendingChangesRequest = this.getUnitholdersBeneficiary();
    const loadLodgedUnitholdersBeneficiaryRequest = this.getUnitholdersBeneficiary(false);

    combineLatest([loadUnitholdersBeneficiaryWithPendingChangesRequest, loadLodgedUnitholdersBeneficiaryRequest])
      .subscribe({
        next: ([unitholdersBeneficiaryWithPendingChanges, lodgedUnitholdersBeneficiary]) => {
          const shareholderDetails = this.state.unitholdersBeneficiaries.shareholderDetails();
          const isPastShareholdersVisible = this.state.unitholdersBeneficiaries.isPastShareholdersVisible();
          const isShowPastShareholdersToggleVisible = unitholdersBeneficiaryWithPendingChanges.some((record) => !record.holdings.length);

          patchState(
            this.state,
            () => ({
              unitholdersBeneficiaries: {
                isLoaded: true,
                isPastShareholdersVisible,
                shareholderDetails,
                isShowPastShareholdersToggleVisible,
                shareholdersWithPendingChanges: unitholdersBeneficiaryWithPendingChanges,
                lodgedShareholders: lodgedUnitholdersBeneficiary,
              }
            })
          );
          this.setIsLoading('isShareholdersLoading', false);
        },
        error: (error) => {
          console.warn('[state.loadShareholders] error: ', error);
          this.toastr.error('Failed to load shareholders', 'Error');
        }
      });
  }

  loadDocuments(): void {
    this.setIsLoading('isPendingDocumentsLoading');

    this.getPendingTrustDocuments()
      .subscribe({
        next: (pendingDocumentRecords) => {
          patchState(this.state, () => ({ pendingDocumentRecords }));
          this.setIsLoading('isPendingDocumentsLoading', false);
        },
        error: (error) => {
          console.warn('[loadTrust] error: ', error);
          this.toastr.error('Failed to load trust documents', 'Error');
        }
      });
  }

  loadIsXpmConnected(): void {
    this.setIsLoading('isIsXpmConnectedLoading');

    this.getIsXpmConnected()
      .subscribe({
        next: (isXpmConnected) => {
          patchState(this.state, () => ({ isXpmConnected }));
          this.setIsLoading('isIsXpmConnectedLoading', false);
        },
        error: (error) => {
          console.warn('[state.loadIsXpmConnected] error: ', error);
          this.toastr.error('Failed to load XPM connection status', 'Error');
        }
      });
  }

  loadNotesTotal(): void {
    this.getNotesTotal()
      .subscribe({
        next: (notesTotal) => this.setNotesTotal(notesTotal),
        error: (error) => {
          console.warn('[state.loadNotesTotal] error: ', error);
          this.toastr.error('Failed to load total notes quantity', 'Error');
        }
      });
  }

  // =====  Requests  =====

  getTrust(applyIncompleteDocuments = true): Observable<Trust> {
    const trustId = this.state().trustId;
    return this.trustsApiService.getTrustProfile(trustId, applyIncompleteDocuments);
  }

  getUnitholdersBeneficiary(applyIncompleteDocuments = true): Observable<SecurityRegistryRecord[]> {
    const trustId = this.state().trustId;
    return this.securityApiService.getSecurityRegistryRecords(trustId, true, '', applyIncompleteDocuments);
  }

  getIsXpmConnected(): Observable<boolean> {
    return this.entityIdentityApiService.isXpmConnected();
  }

  getPendingTrustDocuments(trustId = this.state().trustId): Observable<DocumentRecord[]> {
    return this.documentsApiService.getEntityPendingDocuments(trustId)
      .pipe(
        map((result) => result.records)
      );
  }

  getNotesTotal(trustId = this.state().trustId): Observable<number> {
    return this.notesApiService.getNotesTotal(trustId);
  }

  deleteDocument(documentRecord: DocumentRecord): Observable<void> {
    return this.documentsApiService.deleteDocument(documentRecord.documentId);
  }

  deleteDocumentsList(documentRecords: DocumentRecord[]): Observable<void> {
    const documentsIds = documentRecords.map((documentRecord) => documentRecord.documentId);
    return this.documentsApiService.deleteDocumentsBulk(documentsIds);
  }

  // =====  Request Actions  =====

  openDeleteDocumentModal(documentRecords: DocumentRecord[]): void {
    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const componentInstance = modalRef.componentInstance as CommonActionsModalComponent;

    const isDocumentRecordInSigning = checkIsDocumentRecordInSigning(documentRecords);
    const isBulk = documentRecords.length > 1;
    const pluralS = isBulk ? 's' : '';
    const header = `Delete document${ pluralS }`;
    const theOnlyDocumentName = ` - ${ documentRecords[0].description }`;
    let body = `Are you sure you would like to delete the selected document${ isBulk ? pluralS : theOnlyDocumentName }?`;

    if (isDocumentRecordInSigning) {
      body += ` Deleting the document${ pluralS } will automatically delete the eSigning envelope. Would you like to continue?`;
    }

    const actions: IActionButtonConfig[] = [
      {
        text: 'Delete',
        action: of(true)
      }
    ];

    componentInstance.config = {
      ...componentInstance.config,
      header,
      body,
      actions,
      showCloseBtn: true,
      closeBtnText: 'Cancel'
    };

    modalRef.result
      .then(() => this.performDeleteDocuments(documentRecords), doNothing);
  }

  performDeleteDocuments(documentRecords: DocumentRecord[]): void {
    this.setIsLoading('isPendingDocumentsLoading');
    const request = documentRecords.length > 1
      ? this.deleteDocumentsList(documentRecords)
      : this.deleteDocument(documentRecords[0]);

    request
      .subscribe({
        next: () => {
          this.loadTrust();
          this.loadDocuments();
        },
      });
  }

  // =====  Forms  =====

  openEditTrustDetailsModal(): void {
    if (this.modalOpened()) return;

    const options = { size: 'lg' };
    const confirmationMessage = this.trustsWebformCloseConfirmationMessage;

    const modalRef = this.modalFormsService.openModal(FormEditTrustDetailsComponent, options, confirmationMessage);
    const instance = modalRef.componentInstance as FormEditTrustDetailsComponent;

    instance.trustProfile = structuredClone(this.state.trustWithPendingChanges());

    modalRef.result.then(() => this.loadTrust(), doNothing);
  }

  openTrustDistributionModal(): void {
    if (this.modalOpened()) return;

    const change = new TrustDistribution();
    const modalRef = this.modalFormsService.openTrustModalWithCompany(change, new Company(), false, this.trustsWebformCloseConfirmationMessage);
    const instance = modalRef.componentInstance as FormTrustDistributionComponent;

    instance.formModel = change;
    instance.trustProfile = structuredClone(this.state.trustWithPendingChanges());

    modalRef.result.then(() => this.loadTrust(), doNothing);
  }

  changeTrustee(): void {
    if (this.modalOpened()) return;

    const change = new TrustChangeRelationship({ appointmentType: AppointmentType.Trustees });
    const modalRef = this.openAppliableModal(change);
    const instance = modalRef.componentInstance as FormChangeOfTrusteeComponent;

    instance.trustProfile = structuredClone(this.state.trustWithPendingChanges());

    modalRef.result.then(() => this.loadTrust(), doNothing);
  }

  changeAppointor(): void {
    if (this.modalOpened()) return;
    if (this.state.trustWithPendingChanges().trustType != TrustType.Discretionary) return;

    const change = new TrustChangeRelationship({ appointmentType: AppointmentType.Appointor });
    const modalRef = this.openAppliableModal(change);
    const instance = modalRef.componentInstance as FormChangeOfAppointorComponent;

    instance.trustProfile = structuredClone(this.state.trustWithPendingChanges());

    modalRef.result.then(() => this.loadTrust(), doNothing);
  }

  openAppliableModal(change: EntityChangeData): NgbModalRef {
    const modalRef = this.modalFormsService.openTrustModalWithCompany(change, new Company(), false, this.trustsWebformCloseConfirmationMessage);
    const instance = modalRef.componentInstance as BaseStepperAppliableComponent<any, EntityChangeData>;
    instance.documentApplier = this.trustsApiService.applyChanges.bind(this.trustsApiService);
    return modalRef;
  }
}
